/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Area, AreaChart, CartesianGrid, Legend, XAxis, YAxis,
} from 'recharts';
import { themeColors } from '../../Charts/common';
import { chartsConfig } from './config';
import { BaseLegend } from './Legend';
import { TickProps } from '../../Charts/types';
import { MonthTick } from './MonthTick';

interface BaseAreaChartProps<T extends Record<string, unknown>> {
  xAxisDataKey: keyof T;
  data: T[];
  height?: number;
  width?: number;
  dataKeys: Array<keyof T>;
  colors?: Array<{
    stroke: string;
    fill: string;
    stopOpacity1?: number;
    stopOpacity2?: number;
  }>;
  legendLabels: Partial<Record<keyof T, string>>;
  margin?: {
    top: number;
    right: number;
    left: number;
    bottom: number;
  };
}

const defaultColors = [
  {
    stroke: themeColors.primary,
    fill: themeColors.primary,
    stopOpacity1: 0.3,
    stopOpacity2: 0,
  },
  {
    stroke: themeColors.secondary,
    fill: themeColors.secondary,
    stopOpacity1: 0.5,
    stopOpacity2: 0,
  },
];

export function BaseAreaChart<T extends Record<string, unknown>>(props: BaseAreaChartProps<T>) {
  const {
    xAxisDataKey,
    data,
    dataKeys,
    height,
    width,
    colors = defaultColors,
    legendLabels,
    margin = {
      top: 0, right: 0, left: 0, bottom: 0,
    },
  } = props;

  const ensuredColors = dataKeys.map((key, index) => colors[index % colors.length]);

  return (
    <AreaChart
      width={width}
      height={height ?? 350}
      data={data}
      margin={margin}
    >
      <defs>
        {dataKeys.map((key, index) => (
          <linearGradient id={`color${String(key)}`} x1="0" y1="0" x2="0" y2="1" key={String(key)}>
            <stop
              offset="0%"
              stopColor={ensuredColors[index].fill}
              stopOpacity={ensuredColors[index].stopOpacity1 ?? 0.3}
            />
            <stop
              offset="100%"
              stopColor={ensuredColors[index].fill}
              stopOpacity={ensuredColors[index].stopOpacity2 ?? 0}
            />
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} />
      <XAxis
        dataKey={String(xAxisDataKey)}
        interval={0}
        tickLine={false}
        domain={['dataMin', 'dataMax']}
        // Distance between tick labels and x axis
        stroke={themeColors.gray[400]}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // `Recharts` automatically injects `props` into custom tick component;
        // and we manually type them in the `MonthTick` definition;
        // so we omit this compilation check for avoid warnings about `any` types.
        tick={(_props: TickProps<string>) => MonthTick({
          ..._props,
          dataAmount: data.length,
        })}
        dy={chartsConfig.xLabel.padding}
      />
      <YAxis tick={{ fontSize: chartsConfig.tick.fontSize }} dx={chartsConfig.yLabel.padding} domain={[90, 'auto']} />
      {dataKeys.map((key, index) => (
        <Area
          key={String(key)}
          type="monotone"
          dataKey={String(key)}
          strokeWidth={3}
          stroke={ensuredColors[index].stroke}
          fillOpacity={1}
          fill={`url(#color${String(key)})`}
          isAnimationActive={false}
        />
      ))}

      <Legend
        className="w-100"
        content={(legProps) => (
          <BaseLegend
            payload={legProps?.payload ?? []}
            legendLabels={legendLabels}
          />
        )}
      />
    </AreaChart>
  );
}
